import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { BrandWordmark } from "@/client/components/brand-logo";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import { Input } from "@/client/components/ui/input";
import {
  capacityRequestApi,
  type CapacityRequestView,
} from "@/client/features/capacity-request/capacity-request-api";
import { CapacitySummary } from "@/client/features/capacity-request/capacity-summary";
import { ApiError } from "@/client/lib/api";
import { cn } from "@/client/lib/utils";
import {
  ACCESS_METHODS,
  BANDWIDTH_OPTIONS,
  BOOT_ARTIFACT_SOURCES,
  GPUS_PER_NODE,
  GRACE_CPUS_PER_NODE,
  INFERENCE_MODES,
  INTERCONNECT_OPTIONS,
  MANAGED_OS_STACK,
  MIN_NODES,
  NODES_PER_RACK,
  SERVICE_OFFERINGS,
  TERM_LENGTHS,
  TIMELINES,
  TRAFFIC_PATTERNS,
  USE_CASES,
  capacityRequestSubmissionSchema,
  describeCapacity,
  hasInference,
  hasInfrastructure,
  nodesFromGpus,
  normalizeDraftAnswers,
  rackFraction,
  type CapacityRequestAnswers,
} from "@/shared/capacity-request";
import { STORAGE_TIERS, tierCapacityLabel } from "@/shared/storage-tiers";

type Step = "qualify" | "infrastructure" | "modules" | "inference" | "legal" | "review";

interface FormState {
  company: string;
  contactName: string;
  role: string;
  answers: CapacityRequestAnswers;
}

function fromRequest(request: CapacityRequestView): FormState {
  // Normalize stored answers on load: drop option ids no longer in the catalog and
  // apply migrations (e.g. a legacy `publicIpsNeeded` boolean -> the public-IP
  // access method) so the form state matches what will be submitted.
  return {
    company: request.company ?? "",
    contactName: request.contactName ?? "",
    role: request.role ?? "",
    answers: pruneBranchAnswers(normalizeDraftAnswers(request.answers)),
  };
}

/**
 * Drop branch answers whose service is no longer selected so a deselected
 * offering can't leave stale details in the draft (and in the GTM Slack post).
 * Also strips the "other" free-text when its parent option is unselected: those
 * field errors only render while "other" is picked, so a stale over-length value
 * would otherwise fail the shared schema and block submit behind a hidden error.
 */
function pruneBranchAnswers(answers: CapacityRequestAnswers): CapacityRequestAnswers {
  const services = answers.services ?? [];
  const useCases = answers.useCases ?? [];
  const next = { ...answers };
  if (!hasInfrastructure(answers)) delete next.infrastructure;
  if (!services.includes("managed_os_bare_metal")) delete next.managedOs;
  if (!services.includes("customer_os_bare_metal")) delete next.customerManagedOs;
  if (!hasInference(answers)) delete next.inference;
  if (!services.includes("other")) delete next.otherService;
  if (!useCases.includes("other")) delete next.otherUseCase;
  return next;
}

type FieldErrors = Record<string, string>;

const NO_ERRORS: FieldErrors = {};

/** Run the shared submission schema and map each issue to a dotted field key. */
function collectErrors(state: FormState): FieldErrors {
  const result = capacityRequestSubmissionSchema.safeParse(state);
  if (result.success) return NO_ERRORS;
  const errors: FieldErrors = {};
  for (const issue of result.error.issues) {
    const key = issue.path.join(".");
    if (!(key in errors)) errors[key] = issue.message;
  }
  return errors;
}

/** Which step a given error key belongs to, so we can route the user to it. */
function stepForErrorKey(key: string): Step {
  if (key.startsWith("answers.infrastructure")) return "infrastructure";
  if (key.startsWith("answers.managedOs") || key.startsWith("answers.customerManagedOs")) {
    return "modules";
  }
  if (key.startsWith("answers.inference")) return "inference";
  if (key.startsWith("answers.legal")) return "legal";
  return "qualify";
}

// Reserve a line of height whether or not an error is present so toggling
// validation copy doesn't reflow the row and shove neighboring fields around.
function FieldError({ errors, name }: { errors: FieldErrors; name: string }) {
  const message = errors[name];
  return (
    <p className="text-destructive min-h-4 text-xs" aria-live="polite">
      {message}
    </p>
  );
}

// Ease conditionally revealed fields in instead of snapping them into place, so
// progressive disclosure feels stable rather than jumpy.
function Reveal({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("animate-in fade-in-0 slide-in-from-top-1 duration-200", className)}>
      {children}
    </div>
  );
}

function choiceClass(selected: boolean): string {
  return cn(
    "border-input bg-card hover:bg-accent hover:text-accent-foreground rounded-md border px-3 py-2 text-left text-sm shadow-xs transition-colors",
    selected && "border-primary bg-primary/10 text-primary",
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className="text-sm font-medium">{children}</label>;
}

function FieldHint({ children }: { children: React.ReactNode }) {
  return <p className="text-muted-foreground text-xs">{children}</p>;
}

function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={cn(
        "border-input focus-visible:border-ring focus-visible:ring-ring/50 min-h-24 w-full rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-[3px]",
        props.className,
      )}
    />
  );
}

function Select({
  value,
  onChange,
  children,
}: {
  value: string;
  onChange: (value: string | undefined) => void;
  children: React.ReactNode;
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value === "" ? undefined : event.target.value)}
      className="border-input bg-background focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border px-3 text-sm shadow-xs outline-none focus-visible:ring-[3px]"
    >
      <option value="">Select one</option>
      {children}
    </select>
  );
}

function SingleChoice<T extends string>({
  value,
  options,
  onChange,
}: {
  value?: T;
  options: readonly { id: T; label: string; description?: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          className={choiceClass(value === option.id)}
          onClick={() => onChange(option.id)}
        >
          <span className="block font-medium">{option.label}</span>
          {option.description ? (
            <span className="text-muted-foreground block text-xs">{option.description}</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

function MultiChoice<T extends string>({
  values,
  options,
  onChange,
}: {
  values?: T[];
  options: readonly { id: T; label: string }[];
  onChange: (values: T[]) => void;
}) {
  const selected = values ?? [];
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {options.map((option) => {
        const active = selected.includes(option.id);
        return (
          <button
            key={option.id}
            type="button"
            className={choiceClass(active)}
            onClick={() =>
              onChange(
                active ? selected.filter((value) => value !== option.id) : [...selected, option.id],
              )
            }
          >
            <span className="font-medium">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function RequestShell({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className="bg-muted/30 min-h-svh px-4 py-8">
      <div className={cn("mx-auto grid w-full max-w-5xl gap-6", className)}>
        <header className="grid gap-4">
          {/* Prospects aren't portal users, so the logo points to the marketing
              site, not "/" (which would bounce them to the login page). */}
          <a href="https://mantledc.com" aria-label="Mantle" className="w-fit">
            <BrandWordmark className="w-28" />
          </a>
          <h1 className="text-2xl font-semibold tracking-tight">Capacity Request</h1>
        </header>
        {children}
      </div>
    </div>
  );
}

function EmailGate() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await capacityRequestApi.start(email);
      setSent(true);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Unable to send the link.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RequestShell className="max-w-lg">
      <Card>
        <CardHeader>
          <CardTitle>{sent ? "Check your email" : "Request Mantle capacity"}</CardTitle>
          <CardDescription>
            {sent
              ? "We sent a secure link to continue your request."
              : "Enter your work email. We'll send a secure link so you can save and resume the request."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="grid gap-3">
              <p className="text-sm">Check your email for a link to continue the request.</p>
              <FieldHint>The link expires in 24 hours.</FieldHint>
              <Button type="button" variant="outline" onClick={() => setSent(false)}>
                Use a different email
              </Button>
            </div>
          ) : (
            <form className="grid gap-4" onSubmit={submit}>
              <div className="grid gap-2">
                <FieldLabel>Work email</FieldLabel>
                <Input
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@company.com"
                />
              </div>
              <Button type="submit" disabled={submitting}>
                {submitting ? "Sending..." : "Send secure link"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </RequestShell>
  );
}

function RequestVerifier() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"checking" | "invalid">("checking");
  // The verify token is single-use. Guard against firing the exchange more than
  // once for the same token (React StrictMode's double-invoked effect, a param
  // re-render, or a link prefetch) - a second call would fail on the consumed
  // token and wrongly flip a successful verification to "expired".
  const attemptedToken = useRef<string | null>(null);

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setStatus("invalid");
      return;
    }
    if (attemptedToken.current === token) return;
    attemptedToken.current = token;
    capacityRequestApi
      .verify(token)
      .then(() => navigate("/request", { replace: true }))
      .catch(() => setStatus("invalid"));
  }, [navigate, params]);

  return (
    <RequestShell className="max-w-lg">
      <Card>
        <CardHeader>
          <CardTitle>{status === "checking" ? "Verifying link" : "Link expired"}</CardTitle>
          <CardDescription>
            {status === "checking"
              ? "One moment while we verify your request link."
              : "This request link is invalid or expired. Request a new link to continue."}
          </CardDescription>
        </CardHeader>
        {status === "invalid" ? (
          <CardContent>
            <Button asChild>
              <Link to="/request">Request a new link</Link>
            </Button>
          </CardContent>
        ) : null}
      </Card>
    </RequestShell>
  );
}

/**
 * Bidirectional capacity sizer. The node count is the single stored value;
 * editing nodes, racks, or GPUs all resolve back to a node count, and the
 * others are derived so the three inputs stay in sync.
 */
function CapacitySizer({
  nodeCount,
  onChange,
}: {
  nodeCount?: number;
  onChange: (nodeCount: number | undefined) => void;
}) {
  // The node count is the single stored value; Racks and Grace CPUs are derived
  // read-only. Nodes and Blackwell GPUs keep their own editable text so an
  // in-progress entry (e.g. typing a GPU count) isn't snapped digit-by-digit,
  // but each keystroke still commits the canonical node count immediately so a
  // Continue click (which saves before a blur update would apply) is never stale.
  const [nodesText, setNodesText] = useState(nodeCount === undefined ? "" : String(nodeCount));
  const [gpusText, setGpusText] = useState(
    nodeCount === undefined ? "" : String(nodeCount * GPUS_PER_NODE),
  );
  const editingRef = useRef<null | "nodes" | "gpus">(null);

  const canonicalNodes = nodeCount === undefined ? "" : String(nodeCount);
  const canonicalGpus = nodeCount === undefined ? "" : String(nodeCount * GPUS_PER_NODE);

  // Sync the editable fields to the committed node count, but never overwrite the
  // field the user is actively editing (that would snap their in-progress entry).
  useEffect(() => {
    if (editingRef.current !== "nodes") setNodesText(canonicalNodes);
    if (editingRef.current !== "gpus") setGpusText(canonicalGpus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeCount]);

  const graceCpus = nodeCount === undefined ? "" : nodeCount * GRACE_CPUS_PER_NODE;
  const racks = nodeCount === undefined ? "" : rackFraction(nodeCount);

  const changeNodes = (text: string) => {
    setNodesText(text);
    const value = Number(text.trim());
    if (text.trim() === "" || !Number.isFinite(value)) return onChange(undefined);
    // No MIN_NODES floor here: flooring on each keystroke would turn the first
    // digit of a larger number (e.g. "1" of "18") into the minimum. The 3-node
    // minimum is enforced at submit; a positive integer is fine for a draft.
    onChange(Math.max(1, Math.round(value)));
  };

  const changeGpus = (text: string) => {
    setGpusText(text);
    const value = Number(text.trim());
    if (text.trim() === "" || !Number.isFinite(value)) return onChange(undefined);
    onChange(nodesFromGpus(value));
  };

  // Stop protecting the field and snap its text to the canonical value on blur
  // (e.g. 45 GPUs -> 12 nodes -> 48 GPUs). The node count is already committed.
  const stopEditing = () => {
    editingRef.current = null;
    setNodesText(canonicalNodes);
    setGpusText(canonicalGpus);
  };

  return (
    <div className="grid gap-3">
      <div className="grid gap-3 sm:grid-cols-4">
        <div className="grid gap-1">
          <label className="text-muted-foreground text-xs">Trays</label>
          <Input
            type="number"
            min={MIN_NODES}
            value={nodesText}
            onFocus={() => (editingRef.current = "nodes")}
            onChange={(event) => changeNodes(event.target.value)}
            onBlur={stopEditing}
          />
        </div>
        <div className="grid gap-1">
          <label className="text-muted-foreground text-xs">Racks</label>
          <Input
            type="text"
            readOnly
            value={racks}
            placeholder="—"
            tabIndex={-1}
            className="bg-muted text-muted-foreground"
          />
        </div>
        <div className="grid gap-1">
          <label className="text-muted-foreground text-xs">Blackwell GPUs</label>
          <Input
            type="number"
            min={MIN_NODES * GPUS_PER_NODE}
            step={GPUS_PER_NODE}
            value={gpusText}
            onFocus={() => (editingRef.current = "gpus")}
            onChange={(event) => changeGpus(event.target.value)}
            onBlur={stopEditing}
          />
        </div>
        <div className="grid gap-1">
          <label className="text-muted-foreground text-xs">Grace CPUs</label>
          <Input
            type="text"
            readOnly
            value={graceCpus}
            placeholder="—"
            tabIndex={-1}
            className="bg-muted text-muted-foreground"
          />
        </div>
      </div>
      {nodeCount !== undefined ? (
        <Reveal>
          <p className="text-muted-foreground text-sm">= {describeCapacity(nodeCount)}</p>
        </Reveal>
      ) : null}
    </div>
  );
}

function QualifyStep({
  state,
  setState,
  errors,
}: {
  state: FormState;
  setState: React.Dispatch<React.SetStateAction<FormState>>;
  errors: FieldErrors;
}) {
  const answers = state.answers;
  return (
    <section className="grid gap-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="grid gap-2">
          <FieldLabel>Company</FieldLabel>
          <Input
            value={state.company}
            onChange={(event) => setState((s) => ({ ...s, company: event.target.value }))}
          />
          <FieldError errors={errors} name="company" />
        </div>
        <div className="grid gap-2">
          <FieldLabel>Contact name</FieldLabel>
          <Input
            value={state.contactName}
            onChange={(event) => setState((s) => ({ ...s, contactName: event.target.value }))}
          />
          <FieldError errors={errors} name="contactName" />
        </div>
        <div className="grid gap-2">
          <FieldLabel>Role</FieldLabel>
          <Input
            value={state.role}
            onChange={(event) => setState((s) => ({ ...s, role: event.target.value }))}
          />
          <FieldError errors={errors} name="role" />
        </div>
      </div>

      <div className="grid gap-2">
        <FieldLabel>Use cases</FieldLabel>
        <MultiChoice
          values={answers.useCases}
          options={USE_CASES}
          onChange={(useCases) =>
            setState((s) => ({ ...s, answers: pruneBranchAnswers({ ...s.answers, useCases }) }))
          }
        />
        {answers.useCases?.includes("other") ? (
          <Reveal>
            <Input
              value={answers.otherUseCase ?? ""}
              placeholder="Describe your use case"
              onChange={(event) =>
                setState((s) => ({
                  ...s,
                  answers: { ...s.answers, otherUseCase: event.target.value },
                }))
              }
            />
          </Reveal>
        ) : null}
        <FieldError errors={errors} name="answers.useCases" />
        {answers.useCases?.includes("other") ? (
          <FieldError errors={errors} name="answers.otherUseCase" />
        ) : null}
      </div>

      <div className="grid gap-2">
        <FieldLabel>Services you&apos;re interested in</FieldLabel>
        <MultiChoice
          values={answers.services}
          options={SERVICE_OFFERINGS}
          onChange={(services) =>
            setState((s) => ({ ...s, answers: pruneBranchAnswers({ ...s.answers, services }) }))
          }
        />
        {answers.services?.includes("other") ? (
          <Reveal>
            <Input
              value={answers.otherService ?? ""}
              placeholder="Describe the service you need"
              onChange={(event) =>
                setState((s) => ({
                  ...s,
                  answers: { ...s.answers, otherService: event.target.value },
                }))
              }
            />
          </Reveal>
        ) : null}
        <FieldError errors={errors} name="answers.services" />
        {answers.services?.includes("other") ? (
          <FieldError errors={errors} name="answers.otherService" />
        ) : null}
      </div>

      <div className="grid gap-2">
        <FieldLabel>How much capacity?</FieldLabel>
        <FieldHint>
          Each GB200 tray has {GPUS_PER_NODE} Blackwell GPUs and 2 Grace CPUs. A full NVL72 rack is{" "}
          {NODES_PER_RACK} trays ({NODES_PER_RACK * GPUS_PER_NODE} GPUs) in a single NVLink domain;
          the minimum order is {MIN_NODES} trays. Enter trays, racks, or GPUs and the rest update
          automatically.
        </FieldHint>
        <CapacitySizer
          nodeCount={answers.nodeCount}
          onChange={(nodeCount) =>
            setState((s) => ({ ...s, answers: { ...s.answers, nodeCount } }))
          }
        />
        <FieldError errors={errors} name="answers.nodeCount" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid gap-2">
          <FieldLabel>Timeline</FieldLabel>
          <Select
            value={answers.timeline ?? ""}
            onChange={(timeline) =>
              setState((s) => ({ ...s, answers: { ...s.answers, timeline: timeline as never } }))
            }
          >
            {TIMELINES.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </Select>
          <FieldError errors={errors} name="answers.timeline" />
        </div>
        <div className="grid gap-2">
          <FieldLabel>Term length</FieldLabel>
          <Select
            value={answers.termLength ?? ""}
            onChange={(termLength) =>
              setState((s) => ({
                ...s,
                answers: { ...s.answers, termLength: termLength as never },
              }))
            }
          >
            {TERM_LENGTHS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </Select>
          <FieldError errors={errors} name="answers.termLength" />
        </div>
      </div>
    </section>
  );
}

function InfrastructureStep({
  state,
  setState,
  errors,
}: {
  state: FormState;
  setState: React.Dispatch<React.SetStateAction<FormState>>;
  errors: FieldErrors;
}) {
  const infrastructure = state.answers.infrastructure ?? {};
  const update = (patch: NonNullable<CapacityRequestAnswers["infrastructure"]>) =>
    setState((s) => ({
      ...s,
      answers: { ...s.answers, infrastructure: { ...s.answers.infrastructure, ...patch } },
    }));

  return (
    <section className="grid gap-6">
      <div className="grid gap-2">
        <FieldLabel>Workload interconnect</FieldLabel>
        <Select
          value={infrastructure.interconnect ?? ""}
          onChange={(interconnect) => update({ interconnect: interconnect as never })}
        >
          {INTERCONNECT_OPTIONS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </Select>
        <FieldHint>
          How tightly coupled your jobs are: a single tray, multiple trays in one NVLink domain (one
          NVL72 rack), or many trays across racks on the scale-out fabric.
        </FieldHint>
        <FieldError errors={errors} name="answers.infrastructure.interconnect" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid gap-2">
          <FieldLabel>Bandwidth</FieldLabel>
          <Select
            value={infrastructure.bandwidth ?? ""}
            onChange={(bandwidth) => update({ bandwidth: bandwidth as never })}
          >
            {BANDWIDTH_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </Select>
          <FieldError errors={errors} name="answers.infrastructure.bandwidth" />
        </div>
        <div className="grid gap-2">
          <FieldLabel>High-performance storage</FieldLabel>
          <Select
            value={infrastructure.storageTier ?? ""}
            onChange={(storageTier) => update({ storageTier: storageTier as never })}
          >
            {STORAGE_TIERS.map((tier) => (
              <option key={tier.id} value={tier.id}>
                {tier.name} ({tierCapacityLabel(tier)})
              </option>
            ))}
          </Select>
          <FieldHint>
            Powered by{" "}
            <a
              href="https://www.vastdata.com"
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-4"
            >
              VAST Data
            </a>
            .
          </FieldHint>
          <FieldError errors={errors} name="answers.infrastructure.storageTier" />
        </div>
      </div>

      <div className="grid gap-2">
        <FieldLabel>Access method</FieldLabel>
        <MultiChoice
          values={infrastructure.accessMethods}
          options={ACCESS_METHODS}
          onChange={(accessMethods) => update({ accessMethods })}
        />
        <FieldError errors={errors} name="answers.infrastructure.accessMethods" />
      </div>

      {infrastructure.accessMethods?.includes("public_node_ips") ? (
        <Reveal className="grid gap-2 sm:max-w-xs">
          <FieldLabel>How many public IPs?</FieldLabel>
          <Input
            type="number"
            min={1}
            value={infrastructure.publicIpCount ?? ""}
            onChange={(event) =>
              update({
                publicIpCount: event.target.value ? Number(event.target.value) : undefined,
              })
            }
          />
          <FieldError errors={errors} name="answers.infrastructure.publicIpCount" />
        </Reveal>
      ) : null}
    </section>
  );
}

function ModulesStep({
  state,
  setState,
  errors,
}: {
  state: FormState;
  setState: React.Dispatch<React.SetStateAction<FormState>>;
  errors: FieldErrors;
}) {
  const managedOsSelected = state.answers.services?.includes("managed_os_bare_metal") ?? false;
  const customerOsSelected = state.answers.services?.includes("customer_os_bare_metal") ?? false;
  const customerManagedOs = state.answers.customerManagedOs ?? {};

  const updateCustomerOs = (patch: NonNullable<CapacityRequestAnswers["customerManagedOs"]>) =>
    setState((s) => ({
      ...s,
      answers: {
        ...s.answers,
        customerManagedOs: { ...s.answers.customerManagedOs, ...patch },
      },
    }));

  return (
    <section className="grid gap-6">
      {managedOsSelected ? (
        <Reveal>
          <Card>
            <CardHeader>
              <CardTitle>Managed-OS bare metal</CardTitle>
              <CardDescription>Trays ship with the Mantle-managed baseline below.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              <div className="text-muted-foreground rounded-md border p-3 text-sm">
                {MANAGED_OS_STACK.length ? (
                  <ul className="list-disc space-y-1 pl-5">
                    {MANAGED_OS_STACK.map((component) => (
                      <li key={component.name}>
                        {component.name}
                        {component.version ? ` ${component.version}` : ""}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground">
                    TODO: define OS, driver/CUDA, NCCL, container runtime, and related defaults.
                  </p>
                )}
              </div>
              <div className="grid gap-2">
                <FieldLabel>Anything you need beyond our defaults?</FieldLabel>
                <TextArea
                  value={state.answers.managedOs?.additionalRequirements ?? ""}
                  onChange={(event) =>
                    setState((s) => ({
                      ...s,
                      answers: {
                        ...s.answers,
                        managedOs: { additionalRequirements: event.target.value },
                      },
                    }))
                  }
                />
              </div>
            </CardContent>
          </Card>
        </Reveal>
      ) : null}

      {customerOsSelected ? (
        <Reveal>
          <Card>
            <CardHeader>
              <CardTitle>Customer-managed OS bare metal</CardTitle>
              <CardDescription>
                You bring, boot, and maintain your own OS. Here&apos;s how the handoff works.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              <ul className="text-muted-foreground list-disc space-y-1 rounded-md border p-3 pl-5 text-sm">
                <li>
                  You boot your own OS. Mantle chainloads a custom iPXE menu you provide (configured
                  later in the portal) for your trays; you supply and maintain the OS, drivers, and
                  provisioning stack.
                </li>
                <li>
                  Mantle manages the platform: firmware/BIOS, BMC, the NVLink fabric, network
                  (DHCP/DNS with stable per-tray IPs), power, cooling, and hardware break/fix.
                  Firmware and BIOS are Mantle-managed and not customer-selectable.
                </li>
                <li>
                  Out-of-band access is provided through the portal (browser serial console and
                  power operations) and scoped Redfish via the jump host. Direct BMC/IPMI and BIOS
                  access are not provided.
                </li>
              </ul>
              <div className="grid gap-2">
                <FieldLabel>Where will you serve your OS and boot artifacts?</FieldLabel>
                <SingleChoice
                  value={customerManagedOs.bootArtifactSource}
                  options={BOOT_ARTIFACT_SOURCES}
                  onChange={(bootArtifactSource) => updateCustomerOs({ bootArtifactSource })}
                />
                <FieldHint>
                  Mantle can host boot artifacts on included tenant storage (NFS/HTTP); internet
                  installs pull from your own server.
                </FieldHint>
                <FieldError errors={errors} name="answers.customerManagedOs.bootArtifactSource" />
              </div>
            </CardContent>
          </Card>
        </Reveal>
      ) : null}
    </section>
  );
}

function InferenceStep({
  state,
  setState,
  errors,
}: {
  state: FormState;
  setState: React.Dispatch<React.SetStateAction<FormState>>;
  errors: FieldErrors;
}) {
  const inference = state.answers.inference ?? {};
  const update = (patch: NonNullable<CapacityRequestAnswers["inference"]>) =>
    setState((s) => ({
      ...s,
      answers: { ...s.answers, inference: { ...s.answers.inference, ...patch } },
    }));

  return (
    <section className="grid gap-6">
      <div className="grid gap-2">
        <FieldLabel>Models you plan to serve</FieldLabel>
        <TextArea
          value={inference.models ?? ""}
          placeholder="e.g. Llama 3.1 70B, Qwen2.5 32B, a fine-tuned model, etc."
          onChange={(event) => update({ models: event.target.value })}
        />
        <FieldError errors={errors} name="answers.inference.models" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid gap-2">
          <FieldLabel>Serving type</FieldLabel>
          <Select value={inference.mode ?? ""} onChange={(mode) => update({ mode: mode as never })}>
            {INFERENCE_MODES.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </Select>
          <FieldError errors={errors} name="answers.inference.mode" />
        </div>
        <div className="grid gap-2">
          <FieldLabel>Traffic pattern</FieldLabel>
          <Select
            value={inference.trafficPattern ?? ""}
            onChange={(trafficPattern) => update({ trafficPattern: trafficPattern as never })}
          >
            {TRAFFIC_PATTERNS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </Select>
          <FieldError errors={errors} name="answers.inference.trafficPattern" />
        </div>
      </div>
    </section>
  );
}

function LegalStep({
  state,
  setState,
  errors,
}: {
  state: FormState;
  setState: React.Dispatch<React.SetStateAction<FormState>>;
  errors: FieldErrors;
}) {
  const legal = state.answers.legal ?? {};
  const update = (patch: NonNullable<CapacityRequestAnswers["legal"]>) =>
    setState((s) => ({
      ...s,
      answers: { ...s.answers, legal: { ...s.answers.legal, ...patch } },
    }));
  return (
    <section className="grid gap-4">
      <div className="grid gap-1">
        <label className="flex items-start gap-3 rounded-md border p-3 text-sm">
          <input
            type="checkbox"
            checked={legal.eligibilityAttested === true}
            onChange={(event) => update({ eligibilityAttested: event.target.checked })}
            className="mt-1"
          />
          <span>
            I certify that the represented entity is legally eligible to conduct business in the
            United States and is not subject to applicable sanctions or export restrictions.
          </span>
        </label>
        <FieldError errors={errors} name="answers.legal.eligibilityAttested" />
      </div>
      <div className="grid gap-1">
        <label className="flex items-start gap-3 rounded-md border p-3 text-sm">
          <input
            type="checkbox"
            checked={legal.additionalDiligenceAcknowledged === true}
            onChange={(event) => update({ additionalDiligenceAcknowledged: event.target.checked })}
            className="mt-1"
          />
          <span>
            I acknowledge Mantle may request additional diligence materials depending on opportunity
            size, structure, use case, and ownership.
          </span>
        </label>
        <FieldError errors={errors} name="answers.legal.additionalDiligenceAcknowledged" />
      </div>
    </section>
  );
}

type SaveStatus = "idle" | "unsaved" | "saving" | "saved" | "error";

const AUTOSAVE_DEBOUNCE_MS = 800;

function SaveIndicator({ status, updatedAt }: { status: SaveStatus; updatedAt: string }) {
  const savedAt = new Date(updatedAt).toLocaleString();
  const [text, tone] =
    status === "saving"
      ? ["Saving...", "text-muted-foreground"]
      : status === "unsaved"
        ? ["Unsaved changes", "text-muted-foreground"]
        : status === "error"
          ? ["Couldn't save your draft", "text-destructive"]
          : [`All changes saved · ${savedAt}`, "text-muted-foreground"];
  return (
    <div className={cn("text-xs", tone)} aria-live="polite">
      {text}
    </div>
  );
}

function CapacityRequestForm({ request }: { request: CapacityRequestView }) {
  const [state, setState] = useState<FormState>(() => fromRequest(request));
  const [savedRequest, setSavedRequest] = useState(request);
  const [step, setStep] = useState<Step>("qualify");
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  // Autosave plumbing: the debounce timer, an in-flight guard so concurrent
  // saves can't race, and a ref to the latest form state so a queued save
  // always persists the newest values rather than a stale closure snapshot.
  const stateRef = useRef(state);
  stateRef.current = state;
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savingRef = useRef(false);
  // Set once submission starts so a debounced/in-flight autosave can't PATCH the
  // now-locked draft and surface a spurious 409 error toast after "submitted".
  const submitLockRef = useRef(false);

  const steps = useMemo(() => {
    const next: { id: Step; label: string }[] = [{ id: "qualify", label: "Qualify" }];
    if (hasInfrastructure(state.answers))
      next.push({ id: "infrastructure", label: "Infrastructure" });
    if (hasInfrastructure(state.answers)) next.push({ id: "modules", label: "Offering module" });
    if (hasInference(state.answers)) next.push({ id: "inference", label: "Inference" });
    next.push({ id: "legal", label: "Legal" });
    next.push({ id: "review", label: "Review" });
    return next;
  }, [state.answers]);

  const errors = useMemo(() => collectErrors(state), [state]);
  const stepsWithErrors = useMemo(() => {
    const set = new Set<Step>();
    for (const key of Object.keys(errors)) set.add(stepForErrorKey(key));
    return set;
  }, [errors]);
  const visibleErrors = showErrors ? errors : NO_ERRORS;

  useEffect(() => {
    if (!steps.some((item) => item.id === step)) {
      setStep(steps[0]?.id ?? "qualify");
    }
  }, [step, steps]);

  const currentIndex = Math.max(
    0,
    steps.findIndex((item) => item.id === step),
  );

  const runSave = useCallback(async () => {
    // Once submission has started the draft is locked; drop any pending save.
    if (submitLockRef.current) return;
    // Serialize saves: if one is already running, retry shortly so the latest
    // edits still land without two overlapping PATCHes racing on the server.
    if (savingRef.current) {
      saveTimer.current = setTimeout(() => void runSave(), AUTOSAVE_DEBOUNCE_MS);
      return;
    }
    savingRef.current = true;
    setSaveStatus("saving");
    try {
      const updated = await capacityRequestApi.save(stateRef.current);
      setSavedRequest(updated);
      setSaveStatus("saved");
    } catch (err) {
      // A save that lost the race to submit is expected; don't alarm the user.
      if (submitLockRef.current) return;
      setSaveStatus("error");
      toast.error(err instanceof ApiError ? err.message : "Unable to save.");
    } finally {
      savingRef.current = false;
    }
  }, []);

  // Debounced autosave: every edit marks the draft unsaved and (re)arms the
  // timer. Skip the initial mount so loading an existing draft doesn't re-save.
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setSaveStatus("unsaved");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => void runSave(), AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [state, runSave]);

  // Flush any pending debounce immediately (used at natural persistence points
  // like step navigation and submit) so nothing is left only in the timer.
  const flushSave = useCallback(async () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    if (saveStatus === "unsaved" || saveStatus === "error") {
      await runSave();
    }
  }, [runSave, saveStatus]);

  const goNext = () => {
    const currentStepHasErrors = Object.keys(errors).some((key) => stepForErrorKey(key) === step);
    if (currentStepHasErrors) {
      setShowErrors(true);
      toast.error("Please complete the required fields on this step.");
      return;
    }
    void flushSave();
    setStep(steps[currentIndex + 1]?.id ?? "legal");
  };

  const submit = async () => {
    if (Object.keys(errors).length > 0) {
      setShowErrors(true);
      const firstErroredStep = steps.find((item) => stepsWithErrors.has(item.id))?.id;
      if (firstErroredStep) setStep(firstErroredStep);
      toast.error("Please complete the required fields before submitting.");
      return;
    }
    // Lock out autosave and cancel any pending timer so nothing PATCHes the
    // draft after submission (which would 409 and show a spurious error toast).
    submitLockRef.current = true;
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    setSubmitting(true);
    try {
      const result = await capacityRequestApi.submit(state);
      setSavedRequest(result.request);
      toast.success("Request submitted.");
    } catch (err) {
      // Submission failed, so the draft is still editable; re-enable autosave.
      submitLockRef.current = false;
      toast.error(err instanceof ApiError ? err.message : "Unable to submit.");
    } finally {
      setSubmitting(false);
    }
  };

  if (savedRequest.status === "submitted") {
    return (
      <RequestShell>
        <Card>
          <CardHeader>
            <CardTitle>Request submitted</CardTitle>
            <CardDescription>
              Thanks. The Mantle team will review your request and follow up by email.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            <CapacitySummary data={state} email={request.email} />
            <div>
              <Button asChild variant="outline">
                <a href="https://mantledc.com">Back to Mantle</a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </RequestShell>
    );
  }

  return (
    <RequestShell>
      <Card>
        <CardHeader>
          <CardDescription>
            Signed in with {request.email}. Your progress saves automatically; return from this
            browser to resume.
          </CardDescription>
        </CardHeader>
        <CardContent className="mx-auto grid w-full max-w-3xl gap-6">
          <nav className="flex flex-wrap gap-2" aria-label="Request steps">
            {steps.map((item, index) => {
              const flagged = showErrors && stepsWithErrors.has(item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setStep(item.id)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs font-medium",
                    item.id === step ? "border-primary text-primary" : "text-muted-foreground",
                    flagged && "border-destructive text-destructive",
                  )}
                >
                  {index + 1}. {item.label}
                  {flagged ? <span aria-hidden> ·</span> : null}
                </button>
              );
            })}
          </nav>

          {step === "qualify" ? (
            <QualifyStep state={state} setState={setState} errors={visibleErrors} />
          ) : null}
          {step === "infrastructure" ? (
            <InfrastructureStep state={state} setState={setState} errors={visibleErrors} />
          ) : null}
          {step === "modules" ? (
            <ModulesStep state={state} setState={setState} errors={visibleErrors} />
          ) : null}
          {step === "inference" ? (
            <InferenceStep state={state} setState={setState} errors={visibleErrors} />
          ) : null}
          {step === "legal" ? (
            <LegalStep state={state} setState={setState} errors={visibleErrors} />
          ) : null}
          {step === "review" ? (
            <CapacitySummary
              data={state}
              email={request.email}
              onEdit={(target) => {
                void flushSave();
                setStep(target);
              }}
            />
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
            <SaveIndicator status={saveStatus} updatedAt={savedRequest.updatedAt} />
            <div className="flex gap-2">
              {currentIndex > 0 ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    void flushSave();
                    setStep(steps[currentIndex - 1]?.id ?? "qualify");
                  }}
                >
                  Back
                </Button>
              ) : null}
              {currentIndex < steps.length - 1 ? (
                <Button type="button" onClick={goNext}>
                  Continue
                </Button>
              ) : (
                <Button type="button" onClick={submit} disabled={submitting}>
                  {submitting ? "Submitting..." : "Submit request"}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </RequestShell>
  );
}

export function CapacityRequestPage() {
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState<CapacityRequestView | null>(null);

  useEffect(() => {
    let cancelled = false;
    capacityRequestApi
      .draft()
      .then((draft) => {
        if (!cancelled) setRequest(draft);
      })
      .catch((err) => {
        if (!cancelled && !(err instanceof ApiError && err.isUnauthorized)) {
          toast.error(err instanceof ApiError ? err.message : "Unable to load request.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <RequestShell className="max-w-lg">
        <Card>
          <CardHeader>
            <CardTitle>Loading request</CardTitle>
            <CardDescription>Checking for a saved draft.</CardDescription>
          </CardHeader>
        </Card>
      </RequestShell>
    );
  }

  return request ? <CapacityRequestForm request={request} /> : <EmailGate />;
}

export function CapacityRequestVerifyPage() {
  return <RequestVerifier />;
}
